import { GoogleTags } from "../src/core/components/GoogleTags/GoogleTags"
import { OpenAIAds } from "core/components/OpenAIAds"
import "./global.css"
import { PropsWithChildren, ReactElement, ReactNode, useEffect } from "react"
import dynamic from "next/dynamic"
import CssBaseline from "@mui/material/CssBaseline"
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles"
import { CookiesProvider } from "react-cookie"
import { theme } from "../src/core/theme"
import { QueryClientProvider } from "@tanstack/react-query"
import { queryClient } from "../src/api-hooks/query-client"
import { Toaster } from "react-hot-toast"
import { IntercomProvider } from "react-use-intercom"
import "./app.css"
import "react-phone-input-2/lib/style.css"
import { NextPage } from "next"
import Head from "next/head"
import { CmdkProvider } from "@seamapi/react-cmdk"

import { AppProps } from "next/app"
import { MaybeClerkProvider } from "src/auth/components/MaybeClerkProvider"
import { NuqsAdapter } from "nuqs/adapters/next/pages"
import { Cmdk } from "core/components/Cmdk/Cmdk"
import { useRouter } from "next/router"
import {
  PublicEntryFallback,
  PublicEntryMetadata,
} from "src/auth/components/PublicEntry"

// Lazy-loaded components for faster initial load
const RegisterUserWithPylon = dynamic(
  () =>
    import("account/components/RegisterUserWithPylon").then(
      (mod) => mod.RegisterUserWithPylon
    ),
  { ssr: false }
)

const ChangelogToastGate = dynamic(
  () =>
    import("changelog-toast/components/ChangelogToastGate").then(
      (mod) => mod.ChangelogToastGate
    ),
  { ssr: false }
)

const ReactQueryDevtools =
  process.env.NODE_ENV === "development"
    ? dynamic(
        () =>
          import("@tanstack/react-query-devtools").then(
            (mod) => mod.ReactQueryDevtools
          ),
        { ssr: false }
      )
    : () => null

export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode
}

function defaultGetLayout(page: ReactElement) {
  return page
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

// The following are used to disable SSR around the App component's children.
// This is necessary because the app as a whole does not support SSR, but
// Next assumes that all pages support SSR by default.
const Wrapper = ({ children }: PropsWithChildren<any>) => <>{children}</>
const PublicEntryLoading = () => {
  const { pathname } = useRouter()
  return <PublicEntryFallback pathname={pathname} />
}
const NoSSR = dynamic(async () => Wrapper, {
  ssr: false,
  loading: PublicEntryLoading,
})

function App({ Component, pageProps }: AppPropsWithLayout) {
  const { pathname } = useRouter()
  // Defer FullStory initialization to reduce initial load time
  useEffect(() => {
    const initFullStoryDeferred = () => {
      const orgId = process.env.NEXT_PUBLIC_FULLSTORY_ORG_ID
      if (!orgId) {
        console.warn(
          "Cannot initialize FullStory, NEXT_PUBLIC_FULLSTORY_ORG_ID not set"
        )
        return
      }
      import("@fullstory/browser").then(({ init }) => init({ orgId }))
    }

    if ("requestIdleCallback" in window) {
      requestIdleCallback(initFullStoryDeferred, { timeout: 3000 })
    } else {
      setTimeout(initFullStoryDeferred, 2000)
    }
  }, [])

  if (!process.env.NEXT_PUBLIC_API_HOST) {
    return <p style={{ color: "red" }}>NEXT_PUBLIC_API_HOST not set</p>
  }

  const getLayout = Component.getLayout ?? defaultGetLayout

  return (
    <>
      <OpenAIAds />
      <PublicEntryMetadata pathname={pathname} />
      <GoogleTags />
      <StyledEngineProvider injectFirst>
        <MaybeClerkProvider>
          <QueryClientProvider client={queryClient}>
            <NuqsAdapter>
              <CookiesProvider>
                <IntercomProvider
                  appId={process.env.NEXT_PUBLIC_INTERCOM_APP_ID!}
                >
                  <CmdkProvider>
                    <ThemeProvider theme={theme}>
                      <Head>
                        <link rel="icon" href="/favicon-light.ico" />
                      </Head>
                      <CssBaseline />
                      <Toaster containerStyle={{ zIndex: 999_999 }} />
                      <RegisterUserWithPylon />
                      <ChangelogToastGate />
                      <NoSSR>
                        {getLayout(<Component {...pageProps} />)}
                        <Cmdk />
                      </NoSSR>
                    </ThemeProvider>
                  </CmdkProvider>
                </IntercomProvider>
              </CookiesProvider>
              <ReactQueryDevtools initialIsOpen={false} />
            </NuqsAdapter>
          </QueryClientProvider>
        </MaybeClerkProvider>
      </StyledEngineProvider>
    </>
  )
}

export default App
